"use client";

import { forwardRef, type ReactNode } from "react";
import { Overlay, type OverlayProps } from "./Overlay";

export type DrawerPlacement = "bottom" | "left" | "right";

export type DrawerProps = Omit<OverlayProps, "children" | "role" | "ariaModal" | "ariaLabel" | "ariaLabelledBy" | "ariaDescribedBy" | "panelClassName"> & {
  children: ReactNode;
  placement?: DrawerPlacement;
  className?: string;
  "aria-label"?: string;
  "aria-labelledby"?: string;
  "aria-describedby"?: string;
};

const DEFAULT_OVERLAY_CLASS_NAME = "fixed inset-0 z-[200] flex";
const DEFAULT_PANEL_BASE_CLASS_NAME =
  "relative z-10 overflow-auto border-[var(--fz-overlay-surface-border)] bg-[var(--fz-overlay-surface-bg)] text-[var(--fz-overlay-surface-text)] shadow-[var(--fz-overlay-surface-shadow)] outline-none";

const PLACEMENT_CLASS_NAMES: Record<DrawerPlacement, string> = {
  bottom: "mt-auto max-h-[90vh] w-full rounded-t-[var(--fz-radius)] border border-b-0",
  left: "mr-auto h-full w-full max-w-md rounded-r-[var(--fz-radius)] border border-l-0",
  right: "ml-auto h-full w-full max-w-md rounded-l-[var(--fz-radius)] border border-r-0",
};

export const Drawer = forwardRef<HTMLDivElement, DrawerProps>(function Drawer(
  {
    children,
    placement = "bottom",
    className,
    overlayClassName,
    backdropClassName,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
    "aria-describedby": ariaDescribedBy,
    ...behavior
  },
  ref,
) {
  return (
    <Overlay
      ref={ref}
      {...behavior}
      ariaDescribedBy={ariaDescribedBy}
      ariaLabel={ariaLabel}
      ariaLabelledBy={ariaLabelledBy}
      ariaModal
      backdropClassName={backdropClassName}
      overlayClassName={overlayClassName ?? DEFAULT_OVERLAY_CLASS_NAME}
      panelClassName={className ?? `${DEFAULT_PANEL_BASE_CLASS_NAME} ${PLACEMENT_CLASS_NAMES[placement]}`}
      role="dialog"
    >
      {children}
    </Overlay>
  );
});
